import { useEffect } from "react";
import type { NovelSummary } from "../apiClient.js";
import type { NovelListPagination } from "../components/NovelListMore.js";
import type { TranslationKey } from "./i18n/store.js";

/**
 * Client-side orderings for a novel grid. "recommended" keeps the order the
 * list arrived in (the catalog's server-ranked feed).
 */
export type NovelSortKey = "recommended" | "newest" | "oldest" | "popular";

export interface NovelSortOption {
  key: NovelSortKey;
  labelKey: TranslationKey;
}

/** The author profile's sort control. */
export const PROFILE_SORT_OPTIONS: NovelSortOption[] = [
  { key: "newest", labelKey: "author.sortNewest" },
  { key: "oldest", labelKey: "author.sortOldest" },
  { key: "popular", labelKey: "author.sortPopular" }
];

/** The catalog's: the same orders, led by the ranked feed it loads in. */
export const CATALOG_SORT_OPTIONS: NovelSortOption[] = [
  { key: "recommended", labelKey: "catalog.sortRecommended" },
  ...PROFILE_SORT_OPTIONS
];

export function sortNovels(novels: NovelSummary[], sort: NovelSortKey): NovelSummary[] {
  const copy = [...novels];
  switch (sort) {
    case "recommended":
      return copy;
    case "oldest":
      return copy.sort((a, b) => a.updated_at.localeCompare(b.updated_at));
    case "popular":
      return copy.sort((a, b) => b.views - a.views || b.likes - a.likes);
    case "newest":
    default:
      return copy.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  }
}

/**
 * The catalog and My Novels arrive in pages, and `sortNovels` only reorders the
 * rows already loaded: "Oldest" would show the oldest of the first page, and
 * every "Load more" would drop older or more-viewed cards in above ones the
 * reader had already passed. So any order other than the list's own
 * (`naturalSort`) first loads every remaining page. Returns true while those
 * pages are still on their way — the grid shows its skeleton meanwhile. A failed
 * page ends the wait (the loaded rows show, with the retry button), and a
 * successful retry resumes loading the rest.
 */
export function useFullListForSort(
  sort: NovelSortKey,
  naturalSort: NovelSortKey,
  pagination: NovelListPagination | undefined
): boolean {
  const needsAll = sort !== naturalSort && !!pagination?.hasMore;
  const loadAll = pagination?.loadAll;
  const canLoad = needsAll && !pagination?.loadingMore && !pagination?.moreFailed;
  useEffect(() => {
    if (canLoad) void loadAll?.();
  }, [canLoad, loadAll]);
  return needsAll && !pagination?.moreFailed;
}
